import type { PcmAudio } from "../types";

/** Largest absolute sample value across all channels (0 for silence). */
export function peakAmplitude(audio: PcmAudio): number {
  let peak = 0;
  for (const channel of audio.channelData) {
    for (let i = 0; i < channel.length; i++) {
      const value = Math.abs(channel[i] ?? 0);
      if (value > peak) peak = value;
    }
  }
  return peak;
}

export interface NormalizeOptions {
  /**
   * Target peak in dBFS (decibels relative to full scale). Defaults to -1 dBFS,
   * a small headroom that avoids inter-sample clipping after encoding.
   */
  targetPeakDb?: number;
}

/**
 * Peak-normalize: scale the whole buffer so its loudest sample hits the target
 * peak. Pure. Silent input is returned unchanged (no infinite gain).
 *
 * This is peak normalization, not loudness (LUFS) normalization — predictable,
 * cheap, and clip-free, which is what a simple web util wants.
 */
export function normalize(audio: PcmAudio, options: NormalizeOptions = {}): PcmAudio {
  const targetPeakDb = options.targetPeakDb ?? -1;
  const peak = peakAmplitude(audio);
  if (peak === 0) {
    return { sampleRate: audio.sampleRate, channelData: audio.channelData.map((c) => c.slice()) };
  }

  const targetPeak = 10 ** (targetPeakDb / 20);
  const gain = targetPeak / peak;

  const channelData = audio.channelData.map((channel) => {
    const out = new Float32Array(channel.length);
    for (let i = 0; i < channel.length; i++) {
      out[i] = (channel[i] ?? 0) * gain;
    }
    return out;
  });

  return { sampleRate: audio.sampleRate, channelData };
}
